// Shared validation contract for image URLs used in templates.
// Consumed by both the toolbar's image-URL popover and the TipTap
// email-safe image extension's paste/drop guard.

export type ImageUrlVerdict =
  | { ok: true; warning?: string }
  | { ok: false; error: string };

export const DATA_URL_WARN_BYTES = 200_000;
export const DATA_URL_MAX_BYTES = 1_000_000;

const DATA_URL_STRIP_WARNING =
  'Embedded images are stripped by Gmail and by Outlook on the web. Use an https URL if the reply must render everywhere.';

// Matches data:image/(png|jpeg|jpg|gif|webp), optionally followed by any
// number of ;param segments (e.g. ;charset=utf-8;base64), up to the comma.
// The inner class deliberately excludes ';' (not just ','): with `[^,]*`
// each param segment can itself absorb further ';' characters, so a
// comma-less string like ";;;;...x" has exponentially many ways to be
// partitioned into segments and the regex engine backtracks through all of
// them (catastrophic backtracking / ReDoS). This runs synchronously in the
// paste guard on every doc-changing transaction, so do not "simplify" this
// back to `[^,]*` — excluding ';' too makes each segment boundary
// unambiguous, which keeps matching linear.
const ALLOWED_DATA_IMAGE_MIME = /^data:image\/(png|jpe?g|gif|webp)(?:;[^;,]*)*,/i;
const DATA_URL_SVG = /^data:image\/svg\+xml/i;

// A valid URI scheme: letter, then letters/digits/+/-/. , then ':'.
const SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

const validateDataUrl = (trimmed: string): ImageUrlVerdict => {
  if (DATA_URL_SVG.test(trimmed)) {
    return { ok: false, error: 'SVG images are not allowed — they can carry script.' };
  }

  if (!ALLOWED_DATA_IMAGE_MIME.test(trimmed)) {
    return { ok: false, error: 'Only PNG, JPEG, GIF and WebP data URLs are supported.' };
  }

  const length = trimmed.length;

  if (length > DATA_URL_MAX_BYTES) {
    return {
      ok: false,
      error: 'This embedded image is too large (over 1 MB). Host it and use an https URL instead.',
    };
  }

  if (length > DATA_URL_WARN_BYTES) {
    return {
      ok: true,
      warning: `${DATA_URL_STRIP_WARNING} This also makes the template large and slow to edit.`,
    };
  }

  return { ok: true, warning: DATA_URL_STRIP_WARNING };
};

export const validateImageUrl = (raw: string): ImageUrlVerdict => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';

  if (!trimmed) {
    return { ok: false, error: 'Enter an image URL.' };
  }

  // Only the scheme prefix needs case-insensitive comparison, and a data:
  // URL can be up to ~1 MB — avoid lowercasing (and thus copying) the whole
  // string just to check a few leading characters, since this runs on every
  // doc-changing transaction via the paste guard.
  const head = trimmed.slice(0, 16).toLowerCase();

  if (head.startsWith('data:')) {
    return validateDataUrl(trimmed);
  }

  if (head.startsWith('http://')) {
    return { ok: false, error: 'Use https://. Mail clients block or downgrade insecure images.' };
  }

  if (head.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      if (url.protocol === 'https:' && url.hostname) {
        return { ok: true };
      }
    } catch {
      // fall through to the generic error below
    }
    return { ok: false, error: 'Only https:// image URLs are supported.' };
  }

  // No recognized scheme handled above. Distinguish a relative/schemeless
  // path (//host/x.png, /x.png, host/x.png) from plain garbage input.
  if (!SCHEME_PATTERN.test(trimmed) && trimmed.includes('/')) {
    return {
      ok: false,
      error:
        "Use an absolute https:// URL. A relative URL resolves against the recipient's mail client and can never work.",
    };
  }

  return { ok: false, error: 'Only https:// image URLs are supported.' };
};

/** Cheap predicate used by the editor paste guard. */
export const isAllowedImageSrc = (src: string): boolean => {
  if (!src) return false;
  return validateImageUrl(src).ok;
};
